import { Component } from '@theme/component';

/**
 * <marker-text> — wraps configured keyword phrases inside its text content
 * with `<span class="marker-swipe">` elements, then triggers a staggered
 * highlighter-swipe animation when the host scrolls into view.
 *
 * Attributes:
 *   keywords  — pipe-separated list of phrases (case-insensitive, whole-phrase match)
 *   stagger   — ms between successive highlights (default 200)
 *   duration  — ms per swipe (default 400)
 *   threshold — intersection ratio that triggers playback (default 0.4)
 */
class MarkerText extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.#wrapped) return;
    this.#wrapped = true;

    this.#wrapKeywords();
    this.#observe();
  }

  #wrapped = false;

  #wrapKeywords() {
    const raw = this.getAttribute('keywords') ?? '';
    const keywords = raw
      .split('|')
      .map((k) => k.trim())
      .filter(Boolean);
    if (keywords.length === 0) return;

    keywords.sort((a, b) => b.length - a.length);
    const escaped = keywords.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');

    const stagger = Number(this.getAttribute('stagger')) || 200;
    const duration = Number(this.getAttribute('duration')) || 400;
    this.style.setProperty('--marker-stagger', `${stagger}ms`);
    this.style.setProperty('--marker-duration', `${duration}ms`);

    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.nodeValue || !node.parentElement) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('.marker-swipe')) return NodeFilter.FILTER_REJECT;
        return pattern.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const textNodes = [];
    let current;
    while ((current = walker.nextNode())) textNodes.push(current);

    let index = 0;
    for (const textNode of textNodes) {
      const text = textNode.nodeValue ?? '';
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      pattern.lastIndex = 0;
      let match;
      while ((match = pattern.exec(text)) !== null) {
        if (match.index > cursor) {
          fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)));
        }
        const span = document.createElement('span');
        span.className = 'marker-swipe';
        span.style.setProperty('--marker-index', String(index++));
        span.textContent = match[0];
        fragment.appendChild(span);
        cursor = match.index + match[0].length;
      }
      if (cursor < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(cursor)));
      }
      textNode.replaceWith(fragment);
    }
  }

  #observe() {
    const threshold = Number(this.getAttribute('threshold')) || 0.4;
    const target = this.firstElementChild ?? this;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.setAttribute('data-active', '');
            observer.disconnect();
            return;
          }
        }
      },
      { threshold }
    );
    observer.observe(target);
  }
}

if (!customElements.get('marker-text')) {
  customElements.define('marker-text', MarkerText);
}
